'use client';

import { Language } from '@order-mage/integration-sdk';
import { useQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';
import { ComponentPropsWithoutRef, memo, useMemo } from 'react';
import {
    buildPackageItemByIdMap,
    getHashFromItemIdMap,
    useCartItems,
    useCartItemsFields,
} from '@/entities/cart';
import { useStorefrontItemsIndex, useStorefrontCartMenuData } from '@/entities/product/lib/useStorefrontMenu';
import { QueryApi } from '@/features/query/api';
import { cn } from '@/shared/lib/cn';
import { useSelectedCurrency } from '@/shared/lib/hooks/useSelectedCurrency';
import { Country } from '@/shared/model/Country';
import { Layout } from '@/shared/ui/layout';
import { prefabsStore } from '@/shared/ui/modal/prefabs-store';
import { CartItemRow } from './cart-item-row';
import './cart-items-list-summary-box.scss';

type Props = ComponentPropsWithoutRef<typeof Layout>;

/** Memo: FormProvider re-renders on delivery edits — skip walking rows when props unchanged. */
export const CartItemsListSummaryBox = memo(function CartItemsListSummaryBox(props: Props) {
    const [selectedCurrency] = useSelectedCurrency();
    const fields = useCartItemsFields();
    const items = useCartItems();
    const venueQuery = useQuery(QueryApi.Venue.queries.Venue);
    const locale = useLocale();
    const userLanguage = locale.toUpperCase().replace('-', '_') as Language;
    const selectedCountry = (venueQuery.data?.address?.country as Country) ?? Country.CZ;
    const itemsLookup = useStorefrontItemsIndex();
    const cartMenuData = useStorefrontCartMenuData();
    const idToHash = useMemo(() => getHashFromItemIdMap(itemsLookup), [itemsLookup]);
    const packageItemById = useMemo(
        () => buildPackageItemByIdMap(cartMenuData?.menuLists),
        [cartMenuData?.menuLists],
    );
    const detailAsModal = prefabsStore.getProductDetail()?.openMode === 'modal';

    return (
        <Layout
            {...props}
            className={cn('cart-items-list-summary-box', props.className)}
            data-testid="cart-items-list-summary-box"
        >
            {fields.map((field, index) => (
                <CartItemRow
                    key={field._id}
                    id={field._id}
                    hash={field.hash}
                    quantity={items[index]?.quantity ?? field.quantity}
                    variant={field.variant}
                    addons={field.addons}
                    memberVariants={field.memberVariants}
                    note={field.note}
                    itemsLookup={itemsLookup}
                    idToHash={idToHash}
                    packageItemById={packageItemById}
                    selectedCountry={selectedCountry}
                    selectedCurrency={selectedCurrency}
                    userLanguage={userLanguage}
                    detailAsModal={detailAsModal}
                />
            ))}
        </Layout>
    );
});
